import React, { useMemo, useState } from 'react';
import { useIntl, getLocale } from 'umi';
import classnames from 'classnames';
import { Card } from 'tea-component';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import ChartIconType from '@/pages/intelligence/components/ChartsComponents/ChartIconType';
import Segment from '../Filter/Segment';
import VisualisContext from '../PageJumper';
import { addFormatMessageSafelyHandler } from '../../utils';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import { PER_TYPES, REGION_MARKET_TYPES, GLOBAL_VIEW_METRICS_OPTS_FOR_SEGMENT_LOCALE, VISUALIS_TITLES } from '../../constants';
import type { IBaseProps, IRegionType } from '@/pages/intelligence/types/common';
import type { IPerType, IVisualisMetadata } from '../../types';
import './index.scss';

interface IVisualisProps extends IBaseProps {
  isLoading: boolean;
  perType: IPerType;
  regionType: IRegionType;
  metrics: string;
  data: IVisualisMetadata;
  setMetrics: Function;
  isMobile: boolean;
}

export default function Visualis(props: IVisualisProps) {
  const { isLoading, perType, regionType, metrics, setMetrics, data, style, className, isMobile } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const formatMessageSafely = addFormatMessageSafelyHandler(formatMessage);

  const title = useMemo(() => {
    const metricsName = metrics === 'revenue' ? 'revenue' : 'downloads';
    const regionName = regionType === REGION_MARKET_TYPES.REGION ? 'regions' : 'markets';
    const titlesKeysMap: any = {
      [PER_TYPES.MARKET]: `top_5_genres_by_${metricsName}`,
      [PER_TYPES.GENRE]: `top_5_${regionName}_by_${metricsName}`,
    };
    const titleKey = titlesKeysMap[perType];
    const titleTranslateKey = VISUALIS_TITLES[titleKey];
    return formatMessageSafely({ id: titleTranslateKey });
  }, [locale, metrics, perType, regionType]);

  const tooltipHeaderRightTitle = useMemo(() => {
    if (typeof metrics !== 'string') return '-';
    if (!metrics?.trim()?.length) return '-';
    const metricLocaleKey = GLOBAL_VIEW_METRICS_OPTS_FOR_SEGMENT_LOCALE.find(item => item.value === metrics)?.text || GLOBAL_VIEW_METRICS_OPTS_FOR_SEGMENT_LOCALE?.[0]?.text;
    if (!metricLocaleKey) return '-';
    return formatMessageSafely({ id: metricLocaleKey });
  }, [locale, metrics]);

  const [chartType, setChartType] = useState(ChartType.BarHorizontal);

  // 切换charts类型事件
  const handleChartType = (value: ChartType) => {
    setChartType(value);
  };

  const isNodata = ((chartType: ChartType, data: any) => {
    const fieldsMap: any = {
      [ChartType.BarHorizontal]: 'bars',
      [ChartType.Pie]: 'pies',
    };
    const chartData = data?.[fieldsMap?.[chartType]];
    if (!chartData?.length) return true;
    return false;
  })(chartType, data);

  return (
    <Card style={style} className={classnames('global-view-visualis', 'dbi-mobileGames-card-context', className)}>
      <Card.Body
        title={title}
        operation={
          isLoading || <ChartIconType barHorizontalIconShow={true} barIconShow={false} lineIconShow={false} chartType={chartType} handleChartType={handleChartType} />
        }
      >
        {
          isMobile && <Segment
            key="genreSegment"
            value={metrics}
            customClassname={'metrics-segment'}
            onChange={(payload: string) => {
              // if (isLoading) return;
              setMetrics(payload);
            }}
            opts={GLOBAL_VIEW_METRICS_OPTS_FOR_SEGMENT_LOCALE}
          />
        }
        <Abnormal
          style={{ height: 384, backgroundColor: '#fff' }}
          isLoading={isLoading}
          isNoData={isNodata}
          component={
            <>
              <VisualisContext
                // TopGenrePerMarket页面 并且 regionType 为 market时，横向柱状图title左侧要展示国旗
                showCountryIcon={perType === PER_TYPES.MARKET && regionType === REGION_MARKET_TYPES.MARKET}
                tooltipHeaderRightTitle={tooltipHeaderRightTitle}
                chartType={chartType}
                dataList={data}
              />
            </>
          }
        />
      </Card.Body>
    </Card>
  );
}
